import { useMemo } from 'react'
import { useCellVisualizationsSlice } from 'redux/slices/hooks/useCellVisualizationsSlice'
import { PinnedMorphotype, PlotSelectionState } from 'redux/slices/types'

type MorphotypeCells = PlotSelectionState & { cellIds?: string[] }

export interface SessionMorphotype {
  id: number
  name: string
  cellIds: string[]
  cellIdSet: Set<string>
}

/**
 * Reads a morphotype's cell ids, handling both the optimized stored form
 * (`cells.cellIds`) and the reconstructed form (`cells.points[].cellId`).
 */
export const getMorphotypeCellIds = (morphotype: PinnedMorphotype): string[] => {
  const cells = morphotype.cells as MorphotypeCells
  if (cells?.cellIds?.length) {
    return cells.cellIds.filter((id): id is string => Boolean(id))
  }
  if (cells?.points?.length) {
    return cells.points.map((point) => point.cellId).filter((id): id is string => Boolean(id))
  }
  return []
}

/**
 * The session's user-annotated morphotypes (pinned in the Cell Vis session),
 * each with its cell ids as a set for O(1) membership checks.
 */
export const useSessionMorphotypes = (): SessionMorphotype[] => {
  const {
    cellVisualizations: { pinnedCells },
  } = useCellVisualizationsSlice()

  return useMemo(
    () =>
      (pinnedCells ?? []).map((morphotype) => {
        const cellIds = getMorphotypeCellIds(morphotype)
        return { id: morphotype.id, name: morphotype.name, cellIds, cellIdSet: new Set(cellIds) }
      }),
    [pinnedCells]
  )
}

export default useSessionMorphotypes
